import { useEffect, useMemo, useState } from 'react';
import { useEffectiveOrgId } from '@/stores/useOrgStore';
import { fetchEvents, type EventLogEntry } from '@/lib/events-api';
import { useCurrentRobot } from '@/hooks/useCurrentRobot';
import { resolveRobotIntId } from '@/lib/robot-id-resolver';
import { SectionLabel } from './components/SectionLabel';
import { CycleTimeHero } from './components/CycleTimeHero';
import { StatCard } from './components/StatCard';
import { ActivityLogCard } from './components/ActivityLogCard';
import { WeeklySummaryCard } from './components/WeeklySummaryCard';
import { TrayIcon, ClockIcon } from './components/icons';
import {
  cycleTimes,
  summarize,
  topDestinations,
  toActivityRows,
} from './components/derive';
import { palette } from './components/palette';

const RANGES = [
  { key: '1h',  label: 'Last hour',     ms: 60 * 60 * 1000 },
  { key: '24h', label: 'Last 24h',      ms: 24 * 60 * 60 * 1000 },
  { key: '48h', label: 'Last 48h',      ms: 48 * 60 * 60 * 1000 },
  { key: '7d',  label: 'Last 7 days',   ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '30d', label: 'Last 30 days',  ms: 30 * 24 * 60 * 60 * 1000 },
] as const;

type RangeKey = typeof RANGES[number]['key'];

const OPERATIONAL_HOURS_TARGET = 8;

export default function EventLogPage() {
  const orgId = useEffectiveOrgId();
  const currentRobot = useCurrentRobot();
  const robotKey = currentRobot?.id ?? null;
  const [rangeKey, setRangeKey] = useState<RangeKey>('48h');
  const [events, setEvents] = useState<EventLogEntry[] | null>(null);
  const [weeklyEvents, setWeeklyEvents] = useState<EventLogEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const range = RANGES.find((r) => r.key === rangeKey)!;

  useEffect(() => {
    if (orgId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const end = new Date();
    const start = new Date(end.getTime() - range.ms);

    // Resolve the dashboard's string robot key to the hub's int id; if that
    // lookup fails (offline / 401 / robot not in hub's inventory), fall back
    // to showing the whole org's events instead of erroring.
    const resolved = robotKey
      ? resolveRobotIntId(orgId, robotKey).catch(() => null)
      : Promise.resolve(null);

    void resolved.then((robotId) => {
      if (cancelled) return;
      // Fetch the activity range plus a 30-day window for the weekly summary
      // chart. Could collapse to one fetch when range is already 30d.
      const weekStart = new Date(end.getTime() - 35 * 24 * 60 * 60 * 1000);
      Promise.all([
        fetchEvents({ orgId, robotId, start, end }),
        rangeKey === '30d'
          ? Promise.resolve(null)
          : fetchEvents({ orgId, robotId, start: weekStart, end }),
      ])
        .then(([rows, weekRows]) => {
          if (cancelled) return;
          setEvents(rows);
          setWeeklyEvents(weekRows ?? rows);
        })
        .catch((e) => {
          if (!cancelled) setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    });

    return () => { cancelled = true; };
  }, [orgId, robotKey, range.ms, rangeKey]);

  const eventList = events ?? [];
  const weeklyList = weeklyEvents ?? eventList;

  const summary = useMemo(() => summarize(eventList), [eventList]);
  const destinations = useMemo(() => topDestinations(eventList), [eventList]);
  const activityRows = useMemo(() => toActivityRows(eventList), [eventList]);

  // Cycle-time hero scopes to today regardless of the selected range — matches
  // the design label "CYCLE TIME PER TASK · TODAY".
  const todayBoundary = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [eventList]);
  const cycleData = useMemo(() => {
    const todayEvents = weeklyList.filter(
      (e) => new Date(e.created_at + 'Z').getTime() >= todayBoundary,
    );
    return cycleTimes(todayEvents);
  }, [weeklyList, todayBoundary]);

  const cycleAvg = cycleData.length
    ? Math.round(cycleData.reduce((a, b) => a + b, 0) / cycleData.length)
    : null;

  const yesterdayCycle = useMemo(() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const yStart = todayBoundary - dayMs;
    const yEnd = todayBoundary;
    const ys = weeklyList.filter((e) => {
      const t = new Date(e.created_at + 'Z').getTime();
      return t >= yStart && t < yEnd;
    });
    const cs = cycleTimes(ys, 999);
    return cs.length ? Math.round(cs.reduce((a, b) => a + b, 0) / cs.length) : null;
  }, [weeklyList, todayBoundary]);

  const cycleDelta = cycleAvg != null && yesterdayCycle != null ? cycleAvg - yesterdayCycle : null;

  const tasksCompletedToday = cycleData.length;

  const rangeControl = (
    <div
      className="flex items-center gap-1 rounded-full p-1"
      style={{ border: `1px solid ${palette.hair}`, background: palette.bgAlt }}
    >
      {RANGES.map((r) => {
        const active = rangeKey === r.key;
        return (
          <button
            key={r.key}
            onClick={() => setRangeKey(r.key)}
            className="rounded-full px-3 py-1 text-[11px] font-bold tracking-wide transition-colors"
            style={{
              background: active ? palette.good : 'transparent',
              color: active ? '#fff' : palette.muted,
            }}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto flex max-w-[1100px] flex-col gap-5">
        <SectionLabel>TODAY'S PERFORMANCE</SectionLabel>

        <CycleTimeHero
          data={cycleData}
          avgSeconds={cycleAvg}
          deltaSeconds={cycleDelta}
        />

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <StatCard
            label="TASKS COMPLETED"
            value={tasksCompletedToday}
            icon={<TrayIcon />}
          />
          <StatCard
            label="OPERATIONAL HOURS"
            value="—"
            icon={<ClockIcon />}
            progress={null}
            sub={`Target ${OPERATIONAL_HOURS_TARGET}h`}
          />
        </div>

        <ActivityLogCard
          rows={activityRows}
          summary={summary}
          destinations={destinations}
          rangeLabel={range.label.toLowerCase()}
          rangeControl={rangeControl}
          loading={loading}
          error={error}
        />

        <WeeklySummaryCard events={weeklyList} />
      </div>
    </div>
  );
}
